import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { Link } from "react-router-dom";
import {
  Users, CalendarClock, TrendingUp, AlertTriangle, IndianRupee, Receipt, ArrowUpRight,
} from "lucide-react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const statusChip = {
  scheduled: "bg-[#F4F4F5] text-[#52525B] border-[#E5E5E2]",
  checked_in: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  in_chair: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
  completed: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  no_show: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  cancelled: "bg-[#F4F4F5] text-[#71717A] border-[#E5E5E2] line-through",
};

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="bg-white border border-[#E5E5E2] rounded-md p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#52525B] font-semibold">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold tracking-tight text-[#1C1C1C] font-mono-num">
            {value}
          </div>
          {sub && <div className="text-xs text-[#52525B] mt-1">{sub}</div>}
        </div>
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center"
          style={{ background: accent || "#F4F4F5" }}
        >
          <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E27B58] font-semibold mb-1">Today</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, Doctor.
          </h1>
          <p className="text-sm text-[#52525B] mt-1">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-[#52525B]">
          <span className="kbd">⌘</span><span className="kbd">K</span>
          <span>Global search</span>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={CalendarClock} label="Today's Appts" value={stats?.today_appointments ?? "—"} accent="#0F4C3A" testid="kpi-today-appts" />
        <Kpi icon={Users} label="Patients" value={stats?.total_patients ?? "—"} accent="#1E3A8A" testid="kpi-patients" />
        <Kpi icon={IndianRupee} label="Month Revenue" value={stats ? fmtINR(stats.month_revenue) : "—"} sub={stats ? `Billed ${fmtINR(stats.month_billed)}` : ""} accent="#15803D" testid="kpi-revenue" />
        <Kpi icon={Receipt} label="Pending Bills" value={stats?.pending_bills ?? "—"} accent="#E27B58" testid="kpi-pending" />
        <Kpi icon={AlertTriangle} label="No-Shows Today" value={stats?.no_shows_today ?? "—"} accent="#D93838" testid="kpi-noshow" />
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Today schedule */}
        <div className="lg:col-span-2 bg-white border border-[#E5E5E2] rounded-md">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E2]">
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">Today's Schedule</h2>
              <p className="text-xs text-[#52525B]">{stats?.today_schedule?.length || 0} appointments</p>
            </div>
            <Link to="/scheduler" className="text-xs font-semibold text-[#0F4C3A] hover:text-[#16654F] flex items-center gap-1" data-testid="open-scheduler-btn">
              Open scheduler <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-[#E5E5E2]">
            {(!stats?.today_schedule || stats.today_schedule.length === 0) && (
              <div className="p-8 text-center text-sm text-[#52525B]">
                No appointments today. Head to the scheduler to add one.
              </div>
            )}
            {stats?.today_schedule?.map((a) => (
              <div key={a.id} className="flex items-center gap-4 px-5 py-3 hover:bg-[#FDFDFB]" data-testid={`schedule-row-${a.id}`}>
                <div className="w-16 shrink-0 text-sm font-mono-num text-[#1C1C1C]">
                  {new Date(a.start).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.patient_name}</div>
                  <div className="text-xs text-[#52525B] truncate">{a.reason || "Consultation"}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-semibold border px-2 py-1 rounded-sm ${statusChip[a.status]}`}>
                  {a.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        <div className="bg-white border border-[#E5E5E2] rounded-md">
          <div className="px-5 py-4 border-b border-[#E5E5E2]">
            <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#0F4C3A]" /> Recent Activity
            </h2>
            <p className="text-xs text-[#52525B]">Audit-grade trail</p>
          </div>
          <ul className="divide-y divide-[#E5E5E2] max-h-[520px] overflow-auto">
            {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
              <li className="p-6 text-center text-sm text-[#52525B]">No activity yet.</li>
            )}
            {stats?.recent_activity?.map((r) => (
              <li key={r.id} className="px-5 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{r.action} · {r.entity}</span>
                  <span className="text-[11px] text-[#52525B] font-mono-num">
                    {new Date(r.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                {r.meta && Object.keys(r.meta).length > 0 && (
                  <div className="text-xs text-[#52525B] mt-0.5 font-mono-num">
                    {JSON.stringify(r.meta)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
